import React from 'react';
import {
  ChipSelector, FormField, NumberInput, Select, TextInput, Textarea, useToast,
} from '@parsina/framework';

import { useTranslation } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import { num } from '../../lib/aiPricingView';
import TaskDialog from '../../components/TaskDialog';
import { UPSERT_MODEL } from './queries';
import { dateToInstant, todayIso } from './PriceDialog';

export const PROVIDERS = ['openrouter', 'google', 'openai', 'groq', 'external'];
export const MODALITIES = ['text', 'vision', 'audio_in', 'audio_out', 'embedding'];
const STATUSES = ['active', 'deprecated'];
const MODEL_ID = /^[A-Za-z0-9][A-Za-z0-9._\/:-]*$/;

/**
 * Create or edit a catalogue row (flows F2, F13).
 *
 * The model id is an identifier on the wire — case-sensitive, ASCII, exactly
 * what the adapter sends — so the field is an LTR island that refuses anything
 * else, and it is frozen once the row exists (a renamed model is a new model).
 * A first price version may ride along; the BFF applies it atomically and only
 * for a NEW model, so the field group is hidden when editing.
 */
export default function ModelDialog({ open, onOpenChange, model, onDone }) {
  const { t } = useTranslation();
  const toast = useToast();
  const editing = !!model;

  const [modelId, setModelId] = React.useState('');
  const [provider, setProvider] = React.useState('');
  const [displayName, setDisplayName] = React.useState('');
  const [modalities, setModalities] = React.useState([]);
  const [status, setStatus] = React.useState('active');
  const [notes, setNotes] = React.useState('');
  const [inputUsd, setInputUsd] = React.useState('');
  const [outputUsd, setOutputUsd] = React.useState('');
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setModelId(model?.modelId || '');
    setProvider(model?.provider || '');
    setDisplayName(model?.displayName || '');
    setModalities(model?.modalities || []);
    setStatus(model?.status === 'deprecated' ? 'deprecated' : 'active');
    setNotes(model?.notes || '');
    setInputUsd('');
    setOutputUsd('');
    setError(null);
  }, [open, model]);

  const idInvalid = !!modelId && !MODEL_ID.test(modelId);
  const dirty = editing
    ? provider !== (model.provider || '') || displayName !== (model.displayName || '')
      || notes !== (model.notes || '') || status !== (model.status === 'deprecated' ? 'deprecated' : 'active')
      || modalities.join() !== (model.modalities || []).join()
    : !!(modelId || provider || displayName || notes || modalities.length || inputUsd || outputUsd);

  const submit = async () => {
    setPending(true);
    setError(null);
    try {
      const input = {
        modelId: modelId.trim(),
        provider,
        displayName: displayName.trim() || null,
        modalities,
        status,
        notes: notes.trim() || null,
      };
      if (!editing && (num(inputUsd) != null || num(outputUsd) != null)) {
        input.initialPrice = {
          modelId: modelId.trim(),
          effectiveFrom: dateToInstant(todayIso()),
          inputUsdPerM: num(inputUsd) == null ? null : String(inputUsd),
          outputUsdPerM: num(outputUsd) == null ? null : String(outputUsd),
          source: 'manual',
        };
      }
      await gql(UPSERT_MODEL, { input });
      toast.success(t('aiPricing.models.dialog.success'));
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      setError(errorMessage(e, t));
    } finally {
      setPending(false);
    }
  };

  return (
    <TaskDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? t('aiPricing.models.dialog.editTitle') : t('aiPricing.models.dialog.title')}
      error={error}
      pending={pending}
      dirty={dirty}
      submitLabel={t('aiPricing.models.dialog.submit')}
      submitDisabled={!modelId.trim() || idInvalid || !provider}
      onSubmit={submit}
      data-testid="ai-models-dialog"
    >
      <TextInput
        label={t('aiPricing.models.dialog.modelId')}
        required
        value={modelId}
        onChange={(e) => setModelId(e.target?.value ?? e)}
        disabled={editing}
        dir="ltr"
        helperText={t('aiPricing.models.dialog.modelIdHelp')}
        error={idInvalid ? t('aiPricing.models.dialog.modelIdInvalid') : undefined}
        inputStyle={{ fontFamily: 'monospace' }}
        data-testid="ai-models-dialog-id"
      />
      <div data-testid="ai-models-dialog-provider">
        <Select
          label={t('aiPricing.models.dialog.provider')}
          required
          value={provider}
          onChange={(e) => setProvider(e.target?.value ?? e)}
          options={PROVIDERS.map((p) => ({ value: p, label: t(`aiPricing.models.provider.${p}`) }))}
          placeholder={t('aiPricing.models.dialog.providerPlaceholder')}
        />
      </div>
      <TextInput
        label={t('aiPricing.models.dialog.displayName')}
        value={displayName}
        onChange={(e) => setDisplayName(e.target?.value ?? e)}
        data-testid="ai-models-dialog-name"
      />
      <ChipSelector
        label={t('aiPricing.models.dialog.modalities')}
        multi
        value={modalities}
        onChange={setModalities}
        options={MODALITIES.map((m) => ({ value: m, label: t(`aiPricing.models.modality.${m}`) }))}
      />
      {editing ? (
        <Select
          label={t('aiPricing.models.dialog.status')}
          value={status}
          onChange={(e) => setStatus(e.target?.value ?? e)}
          options={STATUSES.map((s) => ({ value: s, label: t(`aiPricing.models.status.${s}`) }))}
          placeholder={t('aiPricing.models.dialog.status')}
        />
      ) : (
        <FormField label={t('aiPricing.models.dialog.firstPrice')}>
          <div className="grid grid-cols-2 gap-3">
            <NumberInput
              label={t('aiPricing.models.price.input')}
              value={inputUsd}
              onChange={(e) => setInputUsd(e.target?.value ?? e)}
              unit={t('common.units.usd')}
              data-testid="ai-models-dialog-input-usd"
            />
            <NumberInput
              label={t('aiPricing.models.price.output')}
              value={outputUsd}
              onChange={(e) => setOutputUsd(e.target?.value ?? e)}
              unit={t('common.units.usd')}
              data-testid="ai-models-dialog-output-usd"
            />
          </div>
        </FormField>
      )}
      <Textarea
        label={t('aiPricing.models.dialog.notes')}
        value={notes}
        onChange={(e) => setNotes(e.target?.value ?? e)}
        rows={2}
        maxLength={500}
        showCount
      />
    </TaskDialog>
  );
}
